import React, { useEffect, useRef } from "react";
import { City } from "../../services/searchService";
import {
  VerticalType,
  TripType,
  VERTICALS,
  TRIP_TYPES,
  VERTICAL_OPTIONS,
  TRIP_TYPES_BY_VERTICAL,
  DEFAULT_TRIP_TYPE_BY_VERTICAL,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isSourceCityOnly,
  isDestinationCityOnly,
  isReturnLegRequired,
} from "../../constants/tripTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import TimeSelect from "../TimeSelect/TimeSelect";
import PlaceAutocompleteInput, {
  PlaceSelection,
} from "../PlaceAutocompleteInput/PlaceAutocompleteInput";
import CitySelect from "../CitySelect/CitySelect";
import { Stop } from "../../types/stop";

interface SearchSectionProps {
  activeTab: VerticalType;
  setActiveTab: (tab: VerticalType) => void;
  tripType: TripType;
  setTripType: (tripType: TripType) => void;
  source: City | null;
  setSource: (source: City | null) => void;
  destination: City | null;
  setDestination: (destination: City | null) => void;
  departureDate: string;
  setDepartureDate: (date: string) => void;
  returnDate: string;
  setReturnDate: (date: string) => void;
  pickupTime: string;
  setPickupTime: (time: string) => void;
  dropTime: string;
  setDropTime: (time: string) => void;
  sourceSearch: string;
  setSourceSearch: (search: string) => void;
  destSearch: string;
  setDestSearch: (search: string) => void;
  sourceAddress: PlaceSelection | null;
  destinationAddress: PlaceSelection | null;
  onSourcePlaceSelected: (place: PlaceSelection) => void;
  onDestinationPlaceSelected: (place: PlaceSelection) => void;
  allCities: City[];
  onSourceCitySelected: (city: City) => void;
  onDestinationCitySelected: (city: City) => void;
  stops: Stop[];
  onAddStop: () => void;
  onUpdateStop: (id: string, patch: Partial<Stop>) => void;
  onRemoveStop: (id: string) => void;
  onSwapLocations: () => void;
  handleSearch: () => void;
  handleClearFilter: () => void;
  isSearchEnabled: boolean;
  showClearButton: boolean;
  animationEnabled?: boolean;
}

const SearchSection: React.FC<SearchSectionProps> = ({
  activeTab,
  setActiveTab,
  tripType,
  setTripType,
  source,
  setSource,
  destination,
  setDestination,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  pickupTime,
  setPickupTime,
  dropTime,
  setDropTime,
  sourceSearch,
  setSourceSearch,
  destSearch,
  setDestSearch,
  sourceAddress,
  destinationAddress,
  onSourcePlaceSelected,
  onDestinationPlaceSelected,
  allCities,
  onSourceCitySelected,
  onDestinationCitySelected,
  stops,
  onAddStop,
  onUpdateStop,
  onRemoveStop,
  onSwapLocations,
  handleSearch,
  handleClearFilter,
  isSearchEnabled,
  showClearButton,
  animationEnabled = true,
}) => {
  const departureDateRef = useRef<HTMLInputElement>(null);
  const returnDateRef = useRef<HTMLInputElement>(null);

  // Reset whichever field the current vertical/trip-type no longer needs
  // (e.g. Hourly's destination, or the airport-implicit side for Airport).
  useEffect(() => {
    if (!isSourceFieldVisible(activeTab, tripType) && source !== null) {
      setSource(null);
      setSourceSearch("");
    }
    if (!isDestinationFieldVisible(activeTab, tripType) && destination !== null) {
      setDestination(null);
      setDestSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tripType]);

  // Format date as dd/mm/yyyy for display
  const formatDateForDisplay = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const selectVertical = (vertical: VerticalType) => {
    setActiveTab(vertical);
    setTripType(DEFAULT_TRIP_TYPE_BY_VERTICAL[vertical]);
  };

  // Same field-visibility rules as SearchSectionNew - single source of truth
  // lives in constants/tripTypes.ts so the two forms can never diverge again.
  const isPickUpVisible = isSourceFieldVisible(activeTab, tripType);
  const isDestinationVisible = isDestinationFieldVisible(activeTab, tripType);
  const isPickUpCityOnly = isSourceCityOnly(activeTab, tripType);
  const isDestinationCityOnlyField = isDestinationCityOnly(activeTab, tripType);
  const showReturnDateTime = isReturnLegRequired(tripType);
  const isMultiCity =
    activeTab === VERTICALS.INTERCITY && tripType === TRIP_TYPES.MULTICITY;

  const visibleColumnCount =
    2 + (isPickUpVisible ? 1 : 0) + (isDestinationVisible ? 1 : 0);
  const gridColsClass =
    visibleColumnCount === 2
      ? "lg:grid-cols-2"
      : visibleColumnCount === 3
      ? "lg:grid-cols-3"
      : "lg:grid-cols-4";

  const formVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, delay: 0.15 } },
    exit: { opacity: 0, transition: { duration: 0.1 } },
  };

  return (
    <div
      className="bg-white rounded-lg shadow-lg overflow-visible"
      data-aos={animationEnabled ? "fade-up" : ""}
    >
      {/* Vertical tabs */}
      <div className="flex bg-black rounded-t-lg">
        {VERTICAL_OPTIONS.map((vertical) => (
          <button
            key={vertical.value}
            className={`px-6 py-4 font-medium text-sm flex-1 transition-colors duration-200 ${
              activeTab === vertical.value
                ? "bg-gray-300 text-black"
                : "text-white"
            }`}
            onClick={() => selectVertical(vertical.value)}
          >
            {vertical.label}
          </button>
        ))}
      </div>

      {/* Trip-type sub tabs, scoped to the active vertical */}
      <div className="flex bg-gray-800">
        {TRIP_TYPES_BY_VERTICAL[activeTab].map((option) => (
          <button
            key={option.value}
            className={`px-6 py-2 font-medium text-xs flex-1 transition-colors duration-200 ${
              tripType === option.value
                ? "bg-gray-300 text-black"
                : "text-gray-200"
            }`}
            onClick={() => setTripType(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Search form */}
      <div className="p-6 relative pb-14">
        <div className="flex flex-col gap-6">
          <motion.div layout transition={{ duration: 0.3, ease: "easeInOut" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeTab}-${tripType}`}
                variants={formVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Conditional rendering for non-hourly verticals */}
                {activeTab !== VERTICALS.HOURLY && (
                  <>
                    <div
                      className={`grid grid-cols-1 md:grid-cols-2 ${gridColsClass} gap-6`}
                    >
                      {/* Source location */}
                      {isPickUpVisible && (
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            From
                          </label>
                          <div className="flex items-center border rounded-md p-3 bg-white relative">
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {isPickUpCityOnly ? (
                              <CitySelect
                                className="flex-grow min-w-0 outline-none text-gray-700 placeholder-gray-400"
                                placeholder="Select airport city"
                                cities={allCities}
                                value={source}
                                onSelect={onSourceCitySelected}
                              />
                            ) : (
                              <PlaceAutocompleteInput
                                className="flex-grow min-w-0 outline-none text-gray-700 placeholder-gray-400"
                                placeholder="Enter pickup address"
                                value={sourceSearch}
                                onChange={(text) => {
                                  setSourceSearch(text);
                                  if (source) setSource(null);
                                }}
                                onPlaceSelected={onSourcePlaceSelected}
                              />
                            )}
                            {(isPickUpCityOnly ? source : sourceSearch) && (
                              <FontAwesomeIcon
                                icon={faTimes}
                                className="text-gray-400 cursor-pointer hover:text-gray-600 flex-shrink-0 ml-2"
                                onClick={() => {
                                  setSourceSearch("");
                                  setSource(null);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Destination location */}
                      {isDestinationVisible && (
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            To
                          </label>
                          <div className="flex items-center relative">
                            {/* Swap button - only relevant when both fields are visible
                                and interchangeable (Airport's two fields aren't - one must
                                stay a City pick, the other a free address). */}
                            {isPickUpVisible &&
                              !isPickUpCityOnly &&
                              !isDestinationCityOnlyField && (
                              <button
                                className="absolute sm:-left-[25px] -left-[5px] sm:top-1/2 top-[35%] z-10 transform sm:-translate-y-1/2 -translate-y-full bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md hover:bg-blue-700"
                                onClick={onSwapLocations}
                                aria-label="Swap locations"
                              >
                                <svg
                                  className="w-4 h-4 sm:rotate-0 rotate-90"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M9 6L6 9L9 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M15 18L18 15L15 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M6 9H18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M18 15H6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            )}
                            <div className="flex items-center border rounded-md p-3 bg-white flex-grow pl-3">
                              <svg
                                className="w-5 h-5 text-gray-400 mr-2"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {isDestinationCityOnlyField ? (
                                <CitySelect
                                  className="flex-grow min-w-0 outline-none text-gray-700 placeholder-gray-400"
                                  placeholder="Select airport city"
                                  cities={allCities}
                                  value={destination}
                                  onSelect={onDestinationCitySelected}
                                />
                              ) : (
                                <PlaceAutocompleteInput
                                  className="flex-grow min-w-0 outline-none text-gray-700 placeholder-gray-400"
                                  placeholder="Enter drop address"
                                  value={destSearch}
                                  onChange={(text) => {
                                    setDestSearch(text);
                                    if (destination) setDestination(null);
                                  }}
                                  onPlaceSelected={onDestinationPlaceSelected}
                                />
                              )}
                              {(isDestinationCityOnlyField
                                ? destination
                                : destSearch) && (
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  className="text-gray-400 cursor-pointer hover:text-gray-600 flex-shrink-0 ml-2"
                                  onClick={() => {
                                    setDestSearch("");
                                    setDestination(null);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Departure date - always applies */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Departure Date
                        </label>
                        <div className="relative">
                          <div
                            className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                            onClick={() => {
                              if (departureDateRef.current) {
                                departureDateRef.current.showPicker();
                              }
                            }}
                          >
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <input
                              type="text"
                              className="w-full outline-none text-gray-700 placeholder-gray-400"
                              placeholder="dd/mm/yyyy"
                              value={
                                departureDate
                                  ? formatDateForDisplay(departureDate)
                                  : ""
                              }
                              readOnly
                            />
                            <input
                              ref={departureDateRef}
                              type="date"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              value={departureDate}
                              onChange={(e) => setDepartureDate(e.target.value)}
                              min={new Date().toISOString().split("T")[0]}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Pickup time - always applies */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Time
                        </label>
                        <div className="relative">
                          <div className="flex items-center border rounded-md p-3 bg-white">
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M12 6V12L16 14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            <TimeSelect
                              value={pickupTime}
                              onChange={setPickupTime}
                              selectClassName="bg-white text-gray-700 outline-none cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stops - Multi-City only. Just waypoints; no per-stop
                        date/time (see fareCalculator's calculateMultiCityFare -
                        night charges are decided by pickupTime alone, same as
                        every other trip type). */}
                    {isMultiCity && (
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Stops
                        </label>
                        <div className="space-y-4">
                          {stops.map((stop, index) => (
                            <div
                              key={stop.id}
                              className="flex items-center gap-2"
                            >
                              <div className="flex-grow flex items-center border rounded-md p-3 bg-white">
                                <PlaceAutocompleteInput
                                  className="flex-grow min-w-0 outline-none text-gray-700 placeholder-gray-400"
                                  placeholder={`Stop ${index + 1} address`}
                                  value={stop.addressSearch}
                                  onChange={(text) =>
                                    onUpdateStop(stop.id, {
                                      addressSearch: text,
                                      address: null,
                                    })
                                  }
                                  onPlaceSelected={(place) =>
                                    onUpdateStop(stop.id, {
                                      addressSearch: place.address,
                                      address: place,
                                    })
                                  }
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => onRemoveStop(stop.id)}
                                className="text-gray-400 hover:text-red-600 px-1"
                                aria-label={`Remove stop ${index + 1}`}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={onAddStop}
                          className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          + Add Stop
                        </button>
                      </div>
                    )}

                    {/* Second row - Round Trip and Multi-City both need a
                        return date (Multi-City only, for the trip-day count
                        used to bill the per-day km minimum - see
                        fareCalculator's calculateMultiCityFare); Drop Time
                        is Round-Trip only, since Multi-City no longer
                        collects any per-stop/return time. */}
                    {showReturnDateTime && (
                      <div
                        className={`grid grid-cols-1 ${
                          isMultiCity ? "" : "md:grid-cols-2"
                        } gap-6 mt-6`}
                      >
                        {/* Return date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Return Date
                          </label>
                          <div className="relative">
                            <div
                              className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                              onClick={() => {
                                if (returnDateRef.current) {
                                  returnDateRef.current.showPicker();
                                }
                              }}
                            >
                              <svg
                                className="w-5 h-5 text-gray-400 mr-2"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <input
                                type="text"
                                className="w-full outline-none text-gray-700 placeholder-gray-400"
                                placeholder="dd/mm/yyyy"
                                value={
                                  returnDate
                                    ? formatDateForDisplay(returnDate)
                                    : ""
                                }
                                readOnly
                              />
                              <input
                                ref={returnDateRef}
                                type="date"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                value={returnDate}
                                onChange={(e) => setReturnDate(e.target.value)}
                                min={
                                  departureDate ||
                                  new Date().toISOString().split("T")[0]
                                }
                              />
                            </div>
                          </div>
                        </div>

                        {/* Drop time - Round Trip only */}
                        {!isMultiCity && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Drop Time
                            </label>
                            <div className="relative">
                              <div className="flex items-center border rounded-md p-3 bg-white">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                  <path
                                    d="M12 6V12L16 14"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <TimeSelect
                                  value={dropTime}
                                  onChange={setDropTime}
                                  selectClassName="bg-white text-gray-700 outline-none cursor-pointer"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Conditional rendering for hourly vertical */}
                {activeTab === VERTICALS.HOURLY && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Pickup Location */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Location
                      </label>
                      <div className="flex items-center border rounded-md p-3 bg-white relative">
                        <svg
                          className="w-5 h-5 text-gray-400 mr-2"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <PlaceAutocompleteInput
                          className="flex-grow min-w-0 outline-none text-gray-700 placeholder-gray-400"
                          placeholder="Enter pickup address"
                          value={sourceSearch}
                          onChange={(text) => {
                            setSourceSearch(text);
                            if (source) setSource(null);
                          }}
                          onPlaceSelected={onSourcePlaceSelected}
                        />
                        {sourceSearch && (
                          <FontAwesomeIcon
                            icon={faTimes}
                            className="text-gray-400 cursor-pointer hover:text-gray-600 flex-shrink-0 ml-2"
                            onClick={() => {
                              setSourceSearch("");
                              setSource(null);
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Departure Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Departure Date
                      </label>
                      <div className="relative">
                        <div
                          className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                          onClick={() => {
                            if (departureDateRef.current) {
                              departureDateRef.current.showPicker();
                            }
                          }}
                        >
                          <svg
                            className="w-5 h-5 text-gray-400 mr-2"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className="w-full outline-none text-gray-700 placeholder-gray-400"
                            placeholder="dd/mm/yyyy"
                            value={
                              departureDate
                                ? formatDateForDisplay(departureDate)
                                : ""
                            }
                            readOnly
                          />
                          <input
                            ref={departureDateRef}
                            type="date"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={departureDate}
                            onChange={(e) => setDepartureDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Pickup Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Time
                      </label>
                      <div className="relative">
                        <div className="flex items-center border rounded-md p-3 bg-white">
                          <svg
                            className="w-5 h-5 text-gray-400 mr-2"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 6V12L16 14"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          <TimeSelect
                            value={pickupTime}
                            onChange={setPickupTime}
                            selectClassName="bg-white text-gray-700 outline-none cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Search and Clear buttons */}
        <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 flex items-center gap-4">
          <button
            className={`text-white font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap ${
              !isSearchEnabled
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            onClick={handleSearch}
            disabled={!isSearchEnabled}
          >
            Search Cabs
          </button>
          {showClearButton && (
            <button
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap"
              onClick={handleClearFilter}
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchSection;
